import _ from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { request } from 'app/utils';
import { useSchool } from 'features/school';

import {
  getExams,
  clearExamsAction,
  setExamsDetailsAction,
  clearExamsDetailsAction,
  setExamsAction as setExamsActionFromRedux,
  setExamsActionAction,
  setExamsQuestionsListAction,
  clearExamsQuestionsListAction,
  setExamsResultAction,
  clearExamsResultAction,
  setExamsQuestionAction,
  clearExamsQuestionAction,
  setExamsTimeLeftAction,
  clearExamsTimeLeftAction,
  setExamsQuestionCacheAction,
  cleatExamsQuestionCacheAction,
} from './redux/slice';
import {
  ExamsActions,
  ExamsDetailsState,
  ExamsListResponse,
  ExamsQuestionItemState,
  ExamsQuestionsListItemState,
  ExamsQuestionsListResponse,
  ExamsResultState,
  ExamsTimeLeft,
} from './types';

const useExams = () => {
  const dispatch = useDispatch();

  const { exams, examsDetails, examsAction, questions, question, questionCache, result, timeLeft, totalPages, currentPage, itensPerPage } =
    useSelector(getExams);

  const { apiGatewayUrl } = useSchool();

  const setExams = (exams: ExamsListResponse) => {
    dispatch(setExamsActionFromRedux(exams));
  };

  const clearExams = () => {
    dispatch(clearExamsAction());
  };

  const setExamsDetails = (examsDetails: ExamsDetailsState) => {
    dispatch(setExamsDetailsAction(examsDetails));
  };

  const clearExamsDetails = () => {
    dispatch(clearExamsDetailsAction());
  };

  const setExamsAction = (examsAction: ExamsActions) => {
    dispatch(setExamsActionAction(examsAction));
  };

  const setExamsQuestions = (questions: ExamsQuestionsListItemState[]) => {
    dispatch(setExamsQuestionsListAction(questions));
  };

  const clearExamsQuestions = () => {
    dispatch(clearExamsQuestionsListAction());
  };

  const setExamsQuestion = (question: ExamsQuestionItemState) => {
    dispatch(setExamsQuestionAction(question));
  };

  const clearExamsQuestion = () => {
    dispatch(clearExamsQuestionAction());
  };

  const setExamsQuestionCache = (question: ExamsQuestionItemState) => {
    dispatch(setExamsQuestionCacheAction(question));
  };

  const cleatExamsQuestionCache = () => {
    dispatch(cleatExamsQuestionCacheAction());
  };

  const setExamsResult = (result: ExamsResultState) => {
    dispatch(setExamsResultAction(result));
  };

  const clearExamsResult = () => {
    dispatch(clearExamsResultAction());
  };

  const setExamsTimeLeft = (timeLeft: ExamsTimeLeft) => {
    dispatch(setExamsTimeLeftAction(timeLeft));
  };

  const clearExamsTimeLeft = () => {
    dispatch(clearExamsTimeLeftAction());
  };

  const fetchExam = useCallback(
    async (examUuid: string, cb?: (data: ExamsDetailsState) => void) =>
      request<ExamsDetailsState>({
        url: `/api/v1/School/PracticeExam/Get/${examUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setExamsDetails(data);

        if (cb) cb(data);
      }),
    [apiGatewayUrl]
  );

  const startExam = useCallback(
    async (examUuid: string, cb?: (data: { apiResultCode: number; apiResultMessage: string; apiResultData: string }) => void) =>
      await request<{
        apiResultCode: number;
        apiResultMessage: string;
        apiResultData: string;
      }>({
        url: `/api/v1/School/PracticeExam/Start/${examUuid}`,
        method: 'put',
        baseURL: apiGatewayUrl,
      }).then(async ({ data }) => {
        await fetchExam(examUuid); // Renova os dados da avaliação
        setExamsAction(null); // Remove status de iniciando exame

        if (cb) cb(data);
      }),
    [apiGatewayUrl]
  );

  const fetchQuestions = useCallback(
    async (assessmentUuid: string, cb?: (data: ExamsQuestionsListItemState[]) => void) =>
      request<ExamsQuestionsListResponse>({
        url: `/api/v1/School/PracticeExam/QuestionList/${assessmentUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setExamsQuestions(data.questions);

        if (cb) cb(data.questions);
      }),
    [apiGatewayUrl]
  );

  const fetchResult = useCallback(
    async (assessmentUuid: string, cb?: (data: ExamsQuestionsListItemState[]) => void) =>
      request<any>({
        url: `/api/v1/School/PracticeExam/Results/${assessmentUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setExamsResult(data);
      }),
    [apiGatewayUrl]
  );

  const fetchTimeLeft = useCallback(
    async (assessmentUuid: string) => {
      await request<{
        apiResultData: ExamsTimeLeft;
      }>({
        url: `/api/v1/School/PracticeExam/TimeLeft/${assessmentUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setExamsTimeLeft(data.apiResultData);
      });
    },
    [apiGatewayUrl]
  );

  const finalizeExam = useCallback(
    async (
      examUuid: string,
      assessmentUuid: string,
      cb?: (data: { apiResultCode: number; apiResultMessage: string; apiResultData: string }) => void
    ) =>
      await request<{
        apiResultCode: number;
        apiResultMessage: string;
        apiResultData: string;
      }>({
        url: `/api/v1/School/PracticeExam/Finalize/${assessmentUuid}`,
        method: 'put',
        baseURL: apiGatewayUrl,
      }).then(async ({ data }) => {
        await fetchExam(examUuid); // Renova os dados da avaliação
        await fetchResult(assessmentUuid); // Obtém os dados do resultado
        await fetchQuestions(assessmentUuid); // Renova os dados das questoes
        cleatExamsQuestionCache(); // Remove questões do cache
        setExamsAction(null); // Remove status de iniciando exame
        setExamsTimeLeft(undefined!);

        if (cb) cb(data);
      }),
    [apiGatewayUrl]
  );

  const sendQuestionAnswer = useCallback(
    async (assessmentUuid: string, questionUuid: string, answerUuid: string, cb) => {
      setExamsQuestions(
        questions.map((question) => {
          if (questionUuid === question.studentPracticeExamAssessmentQuestionUuid) {
            return { ...question, studentPracticeExamAssessmentQuestionAnswerChoiceUuid: answerUuid };
          }
          return question;
        })
      );

      await request<any>({
        url: `/api/v1/School/PracticeExam/UpdateQuestionAnswer/${assessmentUuid}`,
        method: 'put',
        baseURL: apiGatewayUrl,
        params: {
          studentPracticeExamAssessmentQuestionUuid: questionUuid,
          studentPracticeExamAssessmentQuestionAnswerChoiceUuid: answerUuid,
        },
      }).then(({ data }) => {
        if (cb) cb(data);
      });
    },
    [questions, apiGatewayUrl]
  );

  const getQuestionNavigation = useCallback(
    (questionUuid: string) => {
      const questionIndex = _.findIndex(questions, (question) => {
        return !!question.studentPracticeExamAssessmentQuestionUuid && question.studentPracticeExamAssessmentQuestionUuid === questionUuid;
      });

      let prev: ExamsQuestionsListItemState = undefined!;
      let next: ExamsQuestionsListItemState = undefined!;

      if (questionIndex) {
        prev = questions[questionIndex - 1];
      }

      if (questionIndex < questions?.length - 1) {
        next = questions[questionIndex + 1];
      }

      return {
        prev,
        next,
      };
    },
    [questions]
  );

  const getUnansweredQuestion = useCallback((questions: ExamsQuestionsListItemState[]) => {
    const question = questions.find((question) => question.studentPracticeExamAssessmentQuestionAnswerChoiceUuid === null);
    return question;
  }, []);

  return {
    exams,
    setExams,
    clearExams,
    fetchExam,
    startExam,
    finalizeExam,

    examsDetails,
    setExamsDetails,
    clearExamsDetails,

    examsAction,
    setExamsAction,

    questions,
    setExamsQuestions,
    clearExamsQuestions,
    fetchQuestions,

    question,
    setExamsQuestion,
    clearExamsQuestion,
    sendQuestionAnswer,
    questionCache,
    setExamsQuestionCache,
    cleatExamsQuestionCache,

    getQuestionNavigation,
    getUnansweredQuestion,

    result,
    setExamsResult,
    clearExamsResult,
    fetchResult,

    timeLeft,
    setExamsTimeLeft,
    clearExamsTimeLeft,
    fetchTimeLeft,

    totalPages,
    currentPage,
    itensPerPage,
  };
};

export default useExams;
